import { ISelectOption } from '@/components/form/fields/SelectField';
import { axiosInstance } from '@/utils/libs/axios';
import { TreeNode } from 'primereact/treenode';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { IStepSchema, stepSchema } from './step';
import { IModuleSchema } from './module';
import _ from 'lodash';
import { IUserSchema } from './user';
import { makeModuleOptions } from '@/utils/libs/moduleUtils';

export const testCaseSchema = z.object({
  id: z.string().optional(),
  case_code: z.string().optional(),
  title: z
    .string()
    .max(255, 'must be 255 characters or less')
    .optional()
    .nullable(),
  description: z
    .string()
    .max(255, 'must be 255 characters or less')
    .optional()
    .nullable(),
  precondition: z.string().max(2500, 'must be 2500 characters or less'),
  type: z.string(),
  priority: z.string(),
  tag: z.string(),
  steps: z.array(stepSchema).min(1),
  module_id: z.string(),
  priority_label: z.string().optional(),
  type_label: z.string().optional(),
  tag_label: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  project_id: z.string().optional(),
  parent_id: z.string().optional().nullable(),
  pic_id: z.string({ required_error: 'PIC is required' }).min(1),
});

export const importFileSchema = z.object({
  file: z.any().refine(file => !_.isEmpty(file), 'File is Required'),
});

export type ITestCaseSchema = z.infer<typeof testCaseSchema> & {
  module?: IModuleSchema;
  created_by?: IUserSchema;
  updated_by?: IUserSchema;
  pic?: IUserSchema;
  original_title?: string | null;
};

export type ITypePriorityAndModule = {
  type: ISelectOption[];
  priority: ISelectOption[];
  module: TreeNode[];
};

export type IImportFileSchema = z.infer<typeof importFileSchema>;

export const TestCaseService = {
  reorderStep: (data: ITestCaseSchema): ITestCaseSchema => {
    // re order step
    const steps = [...data.steps];
    for (let index = 0; index < steps.length; index++) {
      const step = steps[index];
      step.position_step = index;
    }

    return data;
  },

  useAll: (params?: Record<string, any>) =>
    useQuery<ITestCaseSchema[]>({
      queryKey: ['test-case', 'all', params],
      queryFn: async () => {
        const response = await axiosInstance.get('/api/crud/test-case/all', {
          params,
        });

        return response.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: ITestCaseSchema) => {
        const response = await axiosInstance.post(
          '/api/crud/test-case',
          TestCaseService.reorderStep(data),
        );

        return response.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ITestCaseSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-case/${data.id}`,
          TestCaseService.reorderStep(data),
        );

        return response.data;
      },
    }),

  useGetTypeAndPriority: (projectId?: string) =>
    useQuery<ITypePriorityAndModule>({
      queryKey: ['type-and-priority'],
      queryFn: async () => {
        const typeResponse = await axiosInstance.get(
          '/api/crud/test-case/type',
        );
        const priorityResponse = await axiosInstance.get(
          '/api/crud/test-case/priority',
        );
        const moduleResponse = await axiosInstance.get('/api/crud/module/all', {
          params: {
            filter: { project_id: projectId },
            sort: 'order',
            asc: true,
          },
        });

        const moduleDatum = moduleResponse.data as IModuleSchema[];
        const modules = makeModuleOptions(moduleDatum);

        return {
          type: typeResponse.data?.data ?? [],
          priority: priorityResponse.data?.data ?? [],
          module: modules,
        };
      },
    }),

  useGet: (id?: string) =>
    useQuery<ITestCaseSchema>({
      queryKey: ['test-case', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(`/api/crud/test-case/${id}`);
        const data = response.data?.data;
        data.steps.sort(
          (a: IStepSchema, b: IStepSchema) => a.position_step - b.position_step,
        );

        return data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(
          `/api/crud/test-case/${id}`,
        );

        return response.data;
      },
    }),

  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-case/${id}/archive`,
        );

        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-case/${id}/unarchive`,
        );

        return response.data;
      },
    }),

  useExport: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.get(
          `/api/crud/test-case/${id}/export`,
          {
            responseType: 'blob',
          },
        );

        return response.data;
      },
    }),

  useExportArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.get(
          `/api/crud/test-case/${id}/export-tc-archive`,
          {
            responseType: 'blob',
          },
        );

        return response.data;
      },
    }),

  useImport: (id: string) =>
    useMutation({
      mutationFn: async (data: IImportFileSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-case/${id}/import`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
      },
    }),

  useDownloadTemplateImport: () =>
    useMutation({
      mutationFn: async () => {
        const response = await axiosInstance.get(
          '/api/crud/test-case/download-template',
          {
            responseType: 'blob',
          },
        );
        return response.data;
      },
    }),
};
